import {
  Sparkles,
  MessageCircle,
  BellRing,
  CalendarCheck,
  ClipboardCheck,
  FileText,
  DollarSign,
  Trophy,
  Archive,
} from "@/lib/icons";

// ═══════════════════════════════════════════════════
// SINGLE SOURCE OF TRUTH
// ═══════════════════════════════════════════════════

export const STAGES = {
  nuevo: {
    db: "nuevo",
    ui: "Primer Contacto",
    description: "Leads recién ingresados que aún no reciben seguimiento.",
    accent: "#38bdf8",
    icon: Sparkles,
    kanbanOrder: 0,
  },
  contactado: {
    db: "contactado",
    ui: "Proceso",
    description: "Primer acercamiento realizado. Pendiente de avanzar.",
    accent: "#0ea5e9",
    icon: MessageCircle,
    kanbanOrder: 1,
  },
  cita_solicitada: {
    db: "cita_solicitada",
    ui: "Solicita cita",
    description:
      "El prospecto quiere visitar. La asesora debe tomar control y agendar.",
    accent: "#d97706",
    icon: BellRing,
    kanbanOrder: 2,
  },
  cita: {
    db: "cita",
    ui: "Citados",
    description: "Visita coordinada con día y hora.",
    accent: "#f59e0b",
    icon: CalendarCheck,
    kanbanOrder: 3,
  },
  interesado: {
    db: "interesado",
    ui: "Entrevista / Examen",
    description: "Entrevista o evaluación programada.",
    accent: "#8b5cf6",
    icon: ClipboardCheck,
    kanbanOrder: 4,
  },
  entrega_resultado: {
    db: "entrega_resultado",
    ui: "Entrega de Resultado",
    description: "Resultado del examen entregado al prospecto.",
    accent: "#10b981",
    icon: FileText,
    kanbanOrder: 5,
  },
  pendiente_pago: {
    db: "pendiente_pago",
    ui: "Pendiente de Pago",
    description: "Aprobado y esperando pago para inscripción.",
    accent: "#f59e0b",
    icon: DollarSign,
    kanbanOrder: 6,
  },
  cierre: {
    db: "cierre",
    ui: "Cierre de inscripción",
    description: "Estado legacy — redirige a Entrega de Resultado.",
    accent: "#22c55e",
    icon: FileText,
    kanbanOrder: null,
    kanbanGroup: "entrega_resultado",
  },
  inscrito: {
    db: "inscrito",
    ui: "Inscrito",
    description: "Alumno inscrito exitosamente.",
    accent: "#16a34a",
    icon: Trophy,
    kanbanOrder: 7,
  },
  perdido: {
    db: "perdido",
    ui: "Perdido",
    description: "Sin interés confirmado o rechazo explícito (manual).",
    accent: "#64748b",
    icon: Archive,
    kanbanOrder: null,
    kanbanGroup: "perdido",
  },
  inactivo: {
    db: "inactivo",
    ui: "Inactivo",
    description: "7+ días sin respuesta. Reaparece automáticamente si vuelve a escribir.",
    accent: "#94a3b8",
    icon: Archive,
    kanbanOrder: null,
    kanbanGroup: "perdido",
  },
  spam: {
    db: "spam",
    ui: "Spam",
    description: "Mensaje no relevante.",
    accent: "#94a3b8",
    icon: Archive,
    kanbanOrder: null,
    kanbanGroup: "perdido",
  },
};

// ═══════════════════════════════════════════════════
// ALIAS MAP (legacy + variantes comunes → stage key)
// ═══════════════════════════════════════════════════

/** @type {Record<string, string>} */
const ALIASES = {
  // → nuevo
  new: "nuevo",
  // → contactado
  primer_contacto: "contactado",
  "primer contacto": "contactado",
  contacto: "contactado",
  validar: "contactado",
  validando: "contactado",
  definir_grado: "contactado",
  asignado: "contactado",
  seguimiento: "contactado",
  en_proceso: "contactado",
  proceso: "contactado",
  // → cita_solicitada
  solicita_cita: "cita_solicitada",
  "solicita cita": "cita_solicitada",
  "cita solicitada": "cita_solicitada",
  // → cita
  agendar_cita: "cita",
  "agendar cita": "cita",
  cita_agendada: "cita",
  agendado: "cita",
  // → interesado
  aplicar_examen: "interesado",
  "aplicar examen": "interesado",
  examen: "interesado",
  evaluacion: "interesado",
  entrevista: "interesado",
  info: "interesado",
  informacion: "interesado",
  // → entrega_resultado
  resultado: "entrega_resultado",
  "entrega resultado": "entrega_resultado",
  entrega: "entrega_resultado",
  // → pendiente_pago
  pago: "pendiente_pago",
  "pendiente pago": "pendiente_pago",
  pendiente: "pendiente_pago",
  cierre_inscripcion: "pendiente_pago",
  "cierre inscripcion": "pendiente_pago",
  "cierre de inscripcion": "pendiente_pago",
  // → inscrito
  finalizado: "inscrito",
  cerrado: "inscrito",
  matriculado: "inscrito",
  reinscripcion: "inscrito",
  // → perdido
  archivado: "perdido",
  baja: "perdido",
  bajas: "perdido",
  rechazada: "perdido",
  rechazado: "perdido",
  sin_interes: "perdido",
  no_interesado: "perdido",
  descartado: "perdido",
  // → inactivo (auto-archivado por inactividad)
  inactive: "inactivo",
  sin_respuesta: "inactivo",
  "sin respuesta": "inactivo",
};

// ═══════════════════════════════════════════════════
// FUNCIONES DERIVADAS
// ═══════════════════════════════════════════════════

export function normalizeStatus(input) {
  if (!input) return "nuevo";

  let clean = String(input).trim();

  // Remover prefijos legacy tipo PAP / PAP_LANES
  clean = clean.replace(/^pap(?:_lanes)?[\s_-]*/i, "");

  // Direct match
  if (STAGES[clean]) return clean;

  const lower = clean.toLowerCase();
  if (STAGES[lower]) return lower;
  if (ALIASES[lower]) return ALIASES[lower];

  // Intentar con underscores
  const underscored = lower.replace(/[\s-]+/g, "_");
  if (STAGES[underscored]) return underscored;
  if (ALIASES[underscored]) return ALIASES[underscored];

  return "nuevo";
}

/**
 * Convierte cualquier input al valor exacto de DB.
 * @param {string|null|undefined} input
 * @returns {string} valor para columna estado_lead
 */
export function toDbStatus(input) {
  const key = normalizeStatus(input);
  return STAGES[key]?.db ?? "nuevo";
}

/**
 * Convierte un valor de DB a la key de stage para el Kanban.
 * Los estados sin columna propia (perdido, spam) → 'archivado'.
 * @param {string|null|undefined} dbValue
 * @returns {string} stage key para agrupación Kanban
 */
export function fromDbStatus(dbValue) {
  const key = normalizeStatus(dbValue);
  const stage = STAGES[key];
  if (!stage) return "nuevo";
  return stage.kanbanGroup ?? key;
}

/**
 * Metadata de un stage por su key.
 * @param {string} key
 * @returns {StageDef}
 */
export function getStageMeta(key) {
  return STAGES[normalizeStatus(key)] ?? STAGES.nuevo;
}

// ═══════════════════════════════════════════════════
// EXPORTS PARA EL KANBAN
// ═══════════════════════════════════════════════════

/** Stages que aparecen como columnas en el Kanban (ordenadas) */
export const KANBAN_STAGES = Object.entries(STAGES)
  .filter(([, s]) => s.kanbanOrder !== null)
  .sort(([, a], [, b]) => a.kanbanOrder - b.kanbanOrder)
  .map(([key, stage]) => ({
    id: key,
    title: stage.ui,
    description: stage.description,
    accent: stage.accent,
    icon: stage.icon,
  }));

/** Order array para compatibilidad legacy */
export const LEAD_STAGE_ORDER = Object.keys(STAGES);

/** Meta object para compatibilidad legacy */
export const LEAD_STAGE_META = Object.fromEntries(
  Object.entries(STAGES).map(([key, stage]) => [
    key,
    {
      id: key,
      title: stage.ui,
      description: stage.description,
      accent: stage.accent,
      icon: stage.icon,
    },
  ]),
);

/** Alias legacy */
export const normalizeLeadStage = normalizeStatus;
